import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  Typography,
  Space,
  Button,
  Steps,
  Input,
  Slider,
  message,
  Alert,
  Breadcrumb,
  Modal,
  Spin,
  Progress,
} from 'antd';
import {
  LinkOutlined,
  RightCircleOutlined,
  ThunderboltOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { api, v2StylesApi } from '../../../api';
import Editor from '../../FactCheck/Editor';
import './styleNew.css';
import '../Generation/step1/generation.css';
import TipsRotator from '../../Common/TipsRotator';
import { styleTips, waitingTips } from '../../Common/tipsData';

const { Title, Text } = Typography;
const { TextArea } = Input;

const StyleNew = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState('url'); // 'url' | 'text'
  const [url, setUrl] = useState('');
  const [articleText, setArticleText] = useState('');
  const [articleBlocks, setArticleBlocks] = useState([]);
  // Editor 初期値を安定化させるための参照（STEP2入場時に一度だけセット）
  const editorInitialBlocksRef = useRef(null);
  const [editorInitialData, setEditorInitialData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [styleConfig, setStyleConfig] = useState(null);
  const [yamlText, setYamlText] = useState('');
  const [styleName, setStyleName] = useState('');
  const [styleMemo, setStyleMemo] = useState('');
  const [progress, setProgress] = useState(0);
  const [styleSummary, setStyleSummary] = useState('');
  // Editor設定はhooks順序を守るためトップレベルでメモ化
  const editorConfig = useMemo(() => ({ holder: 'style-editor' }), []);

  // Slide-in on mount
  const containerRef = useRef(null);
  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      el.classList.add('styleNew-enter');
      // trigger animation
      requestAnimationFrame(() => el.classList.add('styleNew-enter-active'));
    }
    return () => {
      if (el) {
        el.classList.remove('styleNew-enter');
        el.classList.remove('styleNew-enter-active');
      }
    };
  }, []);

  // ローディング/解析中のプログレス演出
  // - スクレイピング(loading): 約3.5秒で90%
  // - LLM解析(analyzing): 約70秒で90%
  useEffect(() => {
    let intervalId;
    let timeoutId;
    if (loading || analyzing) {
      const target = 90;
      const tickMs = 100;
      const totalMs = loading ? 3500 : analyzing ? 70000 : 0;
      const steps = Math.max(1, Math.floor(totalMs / tickMs));
      const delta = target / steps;
      setProgress(0);
      intervalId = setInterval(() => {
        setProgress(prev => {
          const next = prev + delta;
          return next >= target ? target : next;
        });
      }, tickMs);
    } else {
      setProgress(100);
      timeoutId = setTimeout(() => setProgress(0), 500);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [analyzing, loading]);

  // テキストをEditorJSブロックに分割（見出し推定 + 段落）
  const toBlocks = text => {
    if (!text) return [];
    const lines = String(text).split(/\r?\n/);
    const blocks = [];
    let paragraphBuffer = [];
    const flushParagraph = () => {
      if (paragraphBuffer.length > 0) {
        blocks.push({
          type: 'paragraph',
          data: { text: paragraphBuffer.join(' ') },
        });
        paragraphBuffer = [];
      }
    };
    for (const raw of lines) {
      const t = raw.trim();
      if (!t) {
        flushParagraph();
        continue;
      }
      const h3 = t.match(/^###\s+(.*)$/);
      const h2 = t.match(/^##\s+(.*)$/);
      const h1 = t.match(/^#\s+(.*)$/);
      if (h3) {
        flushParagraph();
        blocks.push({ type: 'header', data: { text: h3[1], level: 3 } });
      } else if (h2) {
        flushParagraph();
        blocks.push({ type: 'header', data: { text: h2[1], level: 2 } });
      } else if (h1) {
        flushParagraph();
        blocks.push({ type: 'header', data: { text: h1[1], level: 1 } });
      } else {
        paragraphBuffer.push(t);
      }
    }
    flushParagraph();
    return blocks;
  };

  const canNextStep1 = useMemo(() => {
    if (mode === 'url') return !!url;
    return true; // 直接入力は未入力でもStep2で入力
  }, [mode, url]);

  const goNextFromStep1 = async () => {
    if (!canNextStep1) {
      message.warning('入力内容を確認してください');
      return;
    }
    if (mode === 'url') {
      setLoading(true);
      try {
        // スタイル解析用のインポートAPI（20,000文字対応、EditorJSブロック形式）
        const resp = await v2StylesApi.importFromUrl(url);
        const blocks = resp?.blocks || [];
        setArticleBlocks(blocks);
        // ブロックからプレーンテキストを抽出
        const plain = blocks
          .map(b => b?.data?.text || '')
          .filter(Boolean)
          .join('\n');
        setArticleText(plain);
        // STEP2でのEditor初期データを先行固定
        try {
          editorInitialBlocksRef.current = Array.isArray(blocks) ? blocks : [];
          setEditorInitialData({ blocks: editorInitialBlocksRef.current });
        } catch (_) {
          editorInitialBlocksRef.current = [];
          setEditorInitialData({ blocks: [] });
        }
        setStep(2);
      } catch (e) {
        message.error(
          e?.response?.data?.detail || 'URLの読み込みに失敗しました',
        );
      } finally {
        setLoading(false);
      }
    } else {
      // 直接入力モード: 現在テキストからブロック化して一度だけ固定
      try {
        const init = toBlocks(articleText);
        editorInitialBlocksRef.current = Array.isArray(init) ? init : [];
        setEditorInitialData({ blocks: editorInitialBlocksRef.current });
      } catch (_) {
        editorInitialBlocksRef.current = [];
        setEditorInitialData({ blocks: [] });
      }
      setStep(2);
    }
  };

  const runPreviewAnalysis = async () => {
    if (!articleText || articleText.trim().length === 0) {
      message.warning('原稿テキストを入力してください');
      return;
    }
    setAnalyzing(true);
    try {
      // ブロックからMarkdownを生成し、解析精度を向上
      const markdown = blocksToMarkdown(
        articleBlocks && articleBlocks.length > 0
          ? articleBlocks
          : toBlocks(articleText),
      );
      // 変換フェーズでメトリクスを計測
      const metrics = computeMarkdownMetricsFromText(markdown);
      if (metrics.charCountTotal > 20000) {
        message.warning(
          '文字数が上限（20000文字）を超えています。内容を削減してください',
        );
        return;
      }
      
      // キューに登録
      const queueRes = await v2StylesApi.analyzeStylePreview({
        source_url: mode === 'url' ? url : undefined,
        article_text: markdown || articleText,
        char_count_total: metrics.charCountTotal,
        h2_count: metrics.h2Count,
        h3_count: metrics.h3Count,
        table_count: metrics.tableCount,
        bullet_block_count: metrics.bulletBlockCount,
      });
      
      const jobId = queueRes?.job_id;
      if (!jobId) {
        throw new Error('ジョブIDが取得できませんでした');
      }
      
      // ポーリングで結果を待つ
      const pollInterval = 2000; // 2秒間隔
      const maxAttempts = 150; // 最大5分 (2秒 × 150回)
      let attempts = 0;
      
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        attempts++;
        
        const statusRes = await v2StylesApi.getStyleAnalysisStatus(jobId);
        
        if (statusRes.status === 'completed') {
          const cfg = statusRes?.style_config || null;
          setStyleConfig(cfg);
          // サマリーは解析プロンプトのmetadata.descriptionを使用
          setStyleSummary(cfg?.metadata?.description || '');

          // 表示用に metadata.title/description を除外したYAMLを生成
          const displayCfg = sanitizeConfigForDisplay(cfg);
          const y = await v2StylesApi.convertYaml(displayCfg || {});
          setYamlText(y?.yaml || '');
          setStep(3);
          return;
        } else if (statusRes.status === 'failed') {
          throw new Error(statusRes.error || '解析に失敗しました');
        }
        // "queued" or "processing" の場合は継続
      }
      
      throw new Error('解析がタイムアウトしました。再度お試しください。');
    } catch (e) {
      message.error(e?.response?.data?.detail || e?.message || '解析に失敗しました');
    } finally {
      setAnalyzing(false);
    }
  };
  // EditorJSブロック → Markdown 変換（見出し/段落/リスト/引用/テーブル/リンク）
  const blocksToMarkdown = blocks => {
    if (!Array.isArray(blocks)) return '';
    const lines = [];
    const escPipe = s => String(s ?? '').replace(/\|/g, '\\|');
    const stripHtml = s => String(s ?? '').replace(/<[^>]*>/g, '');
    const emitListItem = (text, index, ordered, indent = 0) => {
      const pad = '  '.repeat(indent);
      if (ordered) {
        lines.push(`${pad}${index + 1}. ${text}`);
      } else {
        lines.push(`${pad}- ${text}`);
      }
    };
    const normalizeListItem = it => {
      if (it == null) return '';
      if (typeof it === 'string') return stripHtml(it);
      if (typeof it === 'object') {
        // Common shapes: { content: string }, { text: string }, nested: { content, items: [] }
        if (typeof it.content === 'string') return stripHtml(it.content);
        if (typeof it.text === 'string') return stripHtml(it.text);
        return stripHtml(JSON.stringify(it));
      }
      return stripHtml(String(it));
    };
    const emitNestedItems = (items, ordered = false, indent = 0) => {
      (items || []).forEach((child, i) => {
        const text = normalizeListItem(child);
        if (text) emitListItem(text, i, ordered, indent);
        // nested
        if (child && typeof child === 'object' && Array.isArray(child.items)) {
          emitNestedItems(child.items, ordered, indent + 1);
        }
      });
    };
    blocks.forEach((b, idx) => {
      if (!b || !b.type) return;
      const type = b.type;
      const data = b.data || {};
      if (type === 'header') {
        const level = Math.min(Math.max(Number(data.level) || 1, 1), 6);
        lines.push(`${'#'.repeat(level)} ${data.text || ''}`);
      } else if (type === 'paragraph') {
        lines.push(`${data.text || ''}`);
      } else if (type === 'list') {
        const ordered = data.style === 'ordered';
        const items = Array.isArray(data.items) ? data.items : [];
        emitNestedItems(items, ordered, 0);
      } else if (type === 'quote') {
        const qt = data.text || '';
        qt.split(/\r?\n/).forEach(row => lines.push(`> ${row}`));
      } else if (type === 'table') {
        const content = Array.isArray(data.content) ? data.content : [];
        if (content.length > 0) {
          const header = content[0] || [];
          lines.push(`| ${header.map(escPipe).join(' | ')} |`);
          lines.push(`| ${header.map(() => '---').join(' | ')} |`);
          for (let r = 1; r < content.length; r += 1) {
            lines.push(`| ${content[r].map(escPipe).join(' | ')} |`);
          }
        }
      } else if (type === 'link' || type === 'linkTool') {
        const href = data.link || data.url || '';
        const title =
          (data.meta && (data.meta.title || data.meta.site_name)) ||
          data.title ||
          href;
        if (href) lines.push(`[${title}](${href})`);
      } else {
        if (data && typeof data.text === 'string') {
          lines.push(data.text);
        }
      }
      if (idx !== blocks.length - 1) lines.push('');
    });
    return lines.join('\n');
  };

  // Markdownメトリクス計測（コードフェンス/HTML<table>除外、改行除去カウント）
  const computeMarkdownMetricsFromText = text => {
    const src = String(text || '');
    const charCountTotal = Array.from(src.replace(/\r?\n/g, '')).length;
    const lines = src.split(/\r?\n/);
    let inCode = false;
    let inHtmlTable = false;
    let h2Count = 0;
    let h3Count = 0;
    let tableCount = 0;
    let bulletBlockCount = 0;
    let tableRun = 0;
    let bulletRun = 0;

    const isBulletLine = line => /^(\s*)(?:- |\* |・|[0-9]+\. )/.test(line);
    const isTableLike = line => /\|/.test(line);

    for (let i = 0; i < lines.length; i += 1) {
      const raw = lines[i];
      const line = raw.trim();

      // コードフェンス
      if (/^```/.test(line)) {
        inCode = !inCode;
        if (tableRun >= 3) tableCount += 1;
        tableRun = 0;
        if (bulletRun >= 2) bulletBlockCount += 1;
        bulletRun = 0;
        continue;
      }
      if (inCode) continue;

      // HTML table
      if (!inHtmlTable && /<table\b/i.test(line)) {
        inHtmlTable = true;
        tableCount += 1;
      }
      if (inHtmlTable && /<\/table>/i.test(line)) {
        inHtmlTable = false;
      }
      if (inHtmlTable) {
        if (bulletRun >= 2) bulletBlockCount += 1;
        bulletRun = 0;
        continue;
      }

      // ATX見出し
      if (/^##\s+/.test(line) && !/^###\s+/.test(line)) h2Count += 1;
      if (/^###\s+/.test(line)) h3Count += 1;
      // Setext H2
      if (
        line.length > 0 &&
        i + 1 < lines.length &&
        /^\s*-{3,}\s*$/.test(lines[i + 1])
      ) {
        h2Count += 1;
      }
      // HTML見出し
      if (/<h2\b[^>]*>/i.test(line)) h2Count += 1;
      if (/<h3\b[^>]*>/i.test(line)) h3Count += 1;

      // パイプ表（コード外・HTML外）
      if (isTableLike(line)) {
        tableRun += 1;
      } else {
        if (tableRun >= 3) tableCount += 1;
        tableRun = 0;
      }

      // 箇条書きブロック
      if (isBulletLine(line)) {
        bulletRun += 1;
      } else {
        if (bulletRun >= 2) bulletBlockCount += 1;
        bulletRun = 0;
      }
    }
    if (tableRun >= 3) tableCount += 1;
    if (bulletRun >= 2) bulletBlockCount += 1;

    return { charCountTotal, h2Count, h3Count, tableCount, bulletBlockCount };
  };

  // 表示用に metadata.title/description を除外
  const sanitizeConfigForDisplay = cfg => {
    try {
      const copy = JSON.parse(JSON.stringify(cfg || {}));
      if (copy?.metadata) {
        delete copy.metadata.title;
        delete copy.metadata.description;
      }
      return copy;
    } catch (_) {
      return cfg;
    }
  };

  // buildStyleSummary は不要（解析プロンプトの要約を使用）

  const saveStyle = async () => {
    if (!styleName.trim()) {
      message.warning('スタイル名を入力してください');
      return;
    }
    if (!styleConfig || Object.keys(styleConfig || {}).length === 0) {
      message.warning('解析結果（スタイル定義）がありません');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: styleName.trim(),
        description: styleMemo.trim() || undefined,
        source_url: mode === 'url' ? url : undefined,
        style_config: styleConfig,
      };
      await v2StylesApi.createStyle(payload);
      message.success('スタイルを保存しました');
      navigate('/aio/style');
    } catch (e) {
      message.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  // Step3 左カラム: 解析結果のスタイル表示（新規生成の「現在のスタイル」カード相当）
  const AnalyzedStyleInfo = () => {
    const data = styleConfig || null;
    if (!data) return null;

    const hs = data.headings_style || data.headings || {};
    const ps = data.prose_style || data.prose || {};
    const tone = (ps && ps.tone) || {};
    const counts = hs.counts || {};
    const lp = hs.length_policy || {};
    const se = ps.sentence_endings || {};
    const ratios = se.ratios_per_100_sentences || {};
    const preferred = (ps.transitions && ps.transitions.preferred) || [];
    const fmt = ps.formatting || {};

    const h2n = counts?.h2_per_article?.typical || '-';
    const h3n = counts?.h3_per_h2?.typical || '-';
    const h2h = lp?.h2_chars?.typical || '-';
    const h3h = lp?.h3_chars?.typical || '-';
    const h2b = lp?.h2_body_chars?.typical || '-';
    const h3b = lp?.h3_body_chars?.typical || '-';
    const dm = ratios?.desu_masu || '-';

    const toNum = v => {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const clamp = v => Math.max(0, Math.min(100, v));

    const parseAvg = s => {
      if (!s || typeof s !== 'string') return 0;
      const parts = s.split('-').map(x => Number(String(x).trim()));
      if (parts.length === 1 && !Number.isNaN(parts[0])) return parts[0];
      if (parts.length >= 2) {
        const a = Number.isNaN(parts[0]) ? 0 : parts[0];
        const b = Number.isNaN(parts[1]) ? a : parts[1];
        return (a + b) / 2;
      }
      return 0;
    };
    const formatRange = s => {
      if (!s || typeof s !== 'string') return s;
      const parts = s.split('-');
      if (parts.length === 1) {
        const n = Number(parts[0]);
        return Number.isNaN(n) ? s : n.toLocaleString();
      }
      const a = Number(parts[0]);
      const b = Number(parts[1]);
      const fa = Number.isNaN(a) ? parts[0] : a.toLocaleString();
      const fb = Number.isNaN(b) ? parts[1] : b.toLocaleString();
      return `${fa}-${fb}`;
    };

    const sentenceLenAvg = parseAvg(
      ps?.rhythm?.sentence_length_chars?.avg || '',
    );
    const sentenceLenPct = Math.max(
      0,
      Math.min(100, Math.round((sentenceLenAvg / 80) * 100)),
    );

    const hsTemp = hs?.temperature || {};
    const aH2 = toNum(hsTemp?.assertiveness?.h2);
    const aH3 = toNum(hsTemp?.assertiveness?.h3);
    const assertAvg =
      aH2 == null && aH3 == null
        ? null
        : clamp(Math.round(((aH2 ?? aH3 ?? 0) + (aH3 ?? aH2 ?? 0)) / 2));

    const toneIntensity = (() => {
      const direct = toNum(ps?.tone?.intensity ?? ps?.intensity);
      if (direct != null) return clamp(direct);
      if (assertAvg != null) return assertAvg;
      const tf = ps?.transitions?.frequency_per_1000w?.typical;
      if (typeof tf === 'string') {
        const parts = tf.split('-').map(x => Number(x.trim()));
        const avg =
          parts.length >= 2
            ? isNaN(parts[1])
              ? parts[0]
              : (parts[0] + parts[1]) / 2
            : parts[0];
        if (!isNaN(avg)) return clamp(Math.round(avg * 10));
      }
      return null;
    })();

    const vocabDensity = (() => {
      const direct = toNum(
        ps?.rhythm?.info_density ??
          ps?.info_density ??
          data?.rhythm?.info_density,
      );
      if (direct != null) return clamp(direct);
      if (sentenceLenAvg) {
        const norm = Math.max(20, Math.min(80, sentenceLenAvg));
        const pct = Math.round(80 - ((norm - 20) / 60) * 60) + 20;
        return clamp(pct);
      }
      return null;
    })();

    const expertise = (() => {
      const direct = toNum(
        ps?.tone?.expertise ?? ps?.expertise ?? data?.tone?.expertise,
      );
      if (direct != null) return clamp(direct);
      if (assertAvg != null) return assertAvg;
      const neutrality = toNum(
        data?.stance_and_conversion?.media_stance?.neutrality,
      );
      if (neutrality != null) return clamp(100 - neutrality);
      return null;
    })();

    const totalPref = (() => {
      const parseMinMax = s => {
        if (!s || typeof s !== 'string') return [0, 0, 0];
        const parts = s.split('-').map(x => Number(String(x).trim()));
        if (parts.length >= 2) {
          const a = Number.isFinite(parts[0]) ? parts[0] : 0;
          const b = Number.isFinite(parts[1]) ? parts[1] : a;
          const lo = Math.min(a, b);
          const hi = Math.max(a, b);
          const mid = (lo + hi) / 2;
          return [lo, hi, mid];
        }
        const v = Number.isFinite(parts[0]) ? parts[0] : 0;
        return [v, v, v];
      };
      const [h2Min, h2Max] = (() => {
        const [lo, hi] = parseMinMax(h2n);
        return [lo, hi];
      })();
      const [h3Min, h3Max, h3Mid] = parseMinMax(h3n);
      const [h2DirectMin, h2DirectMax] = (() => {
        const [lo, hi] = parseMinMax(h2b);
        return [lo, hi];
      })();
      const [h3BodyMin, h3BodyMax, h3BodyMid] = parseMinMax(h3b);
      const ratioProvided = counts?.h2_with_h3_ratio?.typical || '';
      const [ratioMinRaw, ratioMaxRaw] = (() => {
        const [lo, hi] = parseMinMax(ratioProvided);
        return [lo, hi];
      })();
      const clamp01 = v => Math.max(0, Math.min(1, v));
      const ratioMaxUse = ratioProvided ? clamp01(ratioMaxRaw) : 0.8;
      if (!h2Max || !h2DirectMax) return '—';
      const estMinBase = h2Min * (h2DirectMin + 1 * h3Mid * h3BodyMid);
      const estMaxBase =
        h2Max * (h2DirectMax + ratioMaxUse * h3Max * h3BodyMax);
      const minEst = Math.max(0, Math.round(estMinBase / 100) * 100);
      const maxEst = Math.max(minEst, Math.round(estMaxBase / 100) * 100);
      return `${minEst.toLocaleString()}-${maxEst.toLocaleString()}`;
    })();

    return (
      <div>
        <div className="style-kpis">
          <div className="style-kpi">
            <div className="label">想定文字数（記事全体）</div>
            <div className="value">
              {totalPref}
              <span className="unit">文字</span>
            </div>
          </div>
          <div className="style-kpi">
            <div className="label">です/ます比率</div>
            <div className="value">
              {formatRange(dm)}
              <span className="unit">%</span>
            </div>
          </div>
        </div>
        <div className="style-sep"></div>
        <div className="style-grid">
          <div className="head">項目</div>
          <div className="head">H2</div>
          <div className="head">H3</div>
          <div className="cell left">
            <div className="metric">基本設置数</div>
          </div>
          <div className="cell">
            <div className="num">
              {formatRange(h2n)}
              <span className="unit">個</span>
            </div>
          </div>
          <div className="cell">
            <div className="num">
              {formatRange(h3n)}
              <span className="unit">個</span>
            </div>
          </div>
          <div className="cell left">
            <div className="metric">見出し文字数</div>
          </div>
          <div className="cell">
            <div className="num">
              {formatRange(h2h)}
              <span className="unit">文字</span>
            </div>
          </div>
          <div className="cell">
            <div className="num">
              {formatRange(h3h)}
              <span className="unit">文字</span>
            </div>
          </div>
          <div className="cell left">
            <div className="metric">見出し直下の文字数</div>
          </div>
          <div className="cell">
            <div className="num">
              {formatRange(h2b)}
              <span className="unit">文字</span>
            </div>
          </div>
          <div className="cell">
            <div className="num">
              {formatRange(h3b)}
              <span className="unit">文字</span>
            </div>
          </div>
        </div>
        <div className="style-sep"></div>
        <div className="style-bars">
          <div className="barcard">
            <div className="barlabel">文の長さ</div>
            <Slider
              className="viz-slider"
              disabled
              value={sentenceLenPct}
              tooltip={{ open: false }}
            />
            <div className="barvalue">
              {sentenceLenAvg ? `${Math.round(sentenceLenAvg)} 文字/文` : '—'}
            </div>
          </div>
          <div className="barcard">
            <div className="barlabel">トーン</div>
            <Slider
              className="viz-slider"
              disabled
              value={toneIntensity ?? 0}
              tooltip={{ open: false }}
            />
            <div className="barvalue">
              {toneIntensity != null ? `${toneIntensity}%` : '—'}
            </div>
          </div>
          <div className="barcard">
            <div className="barlabel">語彙性</div>
            <Slider
              className="viz-slider"
              disabled
              value={vocabDensity ?? 0}
              tooltip={{ open: false }}
            />
            <div className="barvalue">
              {vocabDensity != null ? `${vocabDensity}%` : '—'}
            </div>
          </div>
          <div className="barcard">
            <div className="barlabel">専門性</div>
            <Slider
              className="viz-slider"
              disabled
              value={expertise ?? 0}
              tooltip={{ open: false }}
            />
            <div className="barvalue">
              {expertise != null ? `${expertise}%` : '—'}
            </div>
          </div>
        </div>
        <div className="style-sep"></div>
        <div className="style-aux-bars">
          <div className="barcard">
            <div className="barlabel">箇条書き</div>
            <div className="num">
              {fmt?.bullets?.frequency_per_1000w?.typical || '0-0'}
              <span className="unit">/1000字</span>
            </div>
          </div>
          <div className="barcard">
            <div className="barlabel">テーブル</div>
            <div className="num">
              {fmt?.tables?.frequency_per_1000w?.typical || '0-0'}
              <span className="unit">/1000字</span>
            </div>
          </div>
        </div>
        <div className="style-note">
          スタイルはAIが一つの基準とするためのもので、あくまで目安となります
        </div>
      </div>
    );
  };

  return (
    <div ref={containerRef} className="styleNew-container">
      <div className="styleNew-inner">
        <Breadcrumb style={{ marginBottom: 8 }}>
          <Breadcrumb.Item>
            <Link to="/aio/dashboard">AIOコンテンツモード</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/aio/style">スタイル管理</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>新規作成</Breadcrumb.Item>
        </Breadcrumb>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 16 }}
        >
          <Col>
            <Space direction="vertical" size={6}>
              <Title
                level={1}
                className="gradient-text"
                style={{ fontSize: '24px', margin: 0 }}
              >
                スタイル新規作成
              </Title>
              <Text type="secondary">
                URL抽出または原稿入力 → 調整 → 解析 → 保存
              </Text>
            </Space>
          </Col>
        </Row>

        <div style={{ marginTop: 24, marginBottom: 40 }}>
          <Steps
            current={step - 1}
            className="v2-steps"
            items={[
              { title: '入力方法' },
              { title: '原稿の確認・調整' },
              { title: '解析結果・保存' },
            ]}
          />
        </div>

        {step === 1 && (
          <div className="v2-form-narrow">
            <div className="v2-intro-box" style={{ marginBottom: 16 }}>
              <Typography.Paragraph
                className="v2-intro-text"
                style={{ margin: 0 }}
              >
                スタイル機能は、文章の見出し構成・文体・リズム・文字数などの「再現性」を高めるためにAIが参考にする基準です。既存記事のURL抽出または原稿入力をもとに、ブランドに合うスタイル定義を作成できます。チケット消費はありません。
              </Typography.Paragraph>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="v2-form-narrow" style={{ paddingBottom: '60px' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Card
                    hoverable
                    onClick={() => setMode('url')}
                    style={{
                      borderColor: mode === 'url' ? '#1677ff' : undefined,
                    }}
                  >
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Title level={4} style={{ margin: 0 }}>
                        URLから抽出
                      </Title>
                      <Text type="secondary">
                        記事URLを入力して本文を抽出します
                      </Text>
                    </Space>
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card
                    hoverable
                    onClick={() => setMode('text')}
                    style={{
                      borderColor: mode === 'text' ? '#1677ff' : undefined,
                    }}
                  >
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Title level={4} style={{ margin: 0 }}>
                        原稿を直接入力
                      </Title>
                      <Text type="secondary">
                        原稿を直接編集できます（未入力でも次へ進めます）
                      </Text>
                    </Space>
                  </Card>
                </Col>
              </Row>
              <Row gutter={12} align="middle" justify="space-between">
                <Col flex="auto">
                  {mode === 'url' && (
                    <Input
                      placeholder="https://example.com/article"
                      value={url}
                      onChange={e => setUrl(e.target.value)}
                      disabled={loading}
                      size="large"
                    />
                  )}
                </Col>
                <Col>
                  <Button
                    type="primary"
                    size="large"
                    onClick={goNextFromStep1}
                    icon={
                      mode === 'url' ? (
                        <LinkOutlined />
                      ) : (
                        <RightCircleOutlined />
                      )
                    }
                  >
                    {mode === 'url' ? 'コンテンツを抽出する' : '原稿入力に進む'}
                  </Button>
                </Col>
              </Row>
            </Space>
          </div>
        )}

        {step === 2 && (
          <div>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <Alert
                type="success"
                showIcon
                message="原稿を確認・調整し、解析を実行してください（戻れません）"
              />
              {/* ブロックエディタ（FactCheckのEditorを流用） */}
              <div
                style={{
                  border: 'none',
                }}
              >
                {editorInitialData && (
                  <Editor
                    initialData={editorInitialData}
                    onChange={data => {
                      try {
                        const newBlocks = data?.blocks || [];
                        const plain = newBlocks
                          .map(b => b?.data?.text || '')
                          .filter(Boolean)
                          .join('\n');
                        // 保存まではDBに書かないので、テキストへ簡易反映
                        setArticleText(plain);
                        setArticleBlocks(newBlocks);
                      } catch (err) {
                        /* noop */
                      }
                    }}
                    config={editorConfig}
                  />
                )}
              </div>
              {/* 解析ボタンはフッター固定バーに移動 */}
            </Space>
          </div>
        )}

        {step === 3 && (
          <div style={{ paddingBottom: '64px' }}>
            <Row gutter={[24, 24]}>
              <Col xs={24} md={14}>
                <Card title="スタイル解析結果" bordered>
                  {(() => {
                    const desc = styleConfig?.metadata?.description || '';
                    return desc ? (
                      <div
                        className="style-summary-card search-intent-card"
                        style={{ marginBottom: 12 }}
                      >
                        <div className="style-summary-text search-intent-text">
                          {desc}
                        </div>
                      </div>
                    ) : null;
                  })()}
                  <AnalyzedStyleInfo />
                </Card>
              </Col>
              <Col xs={24} md={10}>
                <div style={{ marginTop: 8 }}>
                  <Text strong>スタイル名</Text>
                  <Input
                    placeholder="例: ビジネスブログ標準"
                    value={styleName}
                    onChange={e => setStyleName(e.target.value)}
                    style={{ marginTop: 6, marginBottom: 12 }}
                    className={!styleName?.trim() ? 'blink-border' : undefined}
                  />
                  <Text strong>メモ</Text>
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    このスタイルの用途や特徴を自由に記載してください（任意）
                  </Text>
                  <TextArea
                    placeholder="例: B2Bブログ向け。専門性は高めだが読みやすさ重視。"
                    value={styleMemo}
                    onChange={e => setStyleMemo(e.target.value)}
                    rows={6}
                    style={{ marginTop: 6, marginBottom: 16 }}
                  />
                  <Alert
                    type="info"
                    showIcon
                    message="スタイルの調整は後から管理画面でいつでも行えます。まずは名前を付けて保存しましょう。"
                  />
                </div>
              </Col>
            </Row>
          </div>
        )}
        {/* 解析/保存のフッター固定バー */}
        <div className="styleNew-footerbar">
          {step === 2 && (
            <Button
              type="primary"
              size="large"
              onClick={runPreviewAnalysis}
              loading={analyzing}
              disabled={analyzing}
              icon={<ThunderboltOutlined />}
            >
              原稿を解析する
            </Button>
          )}
          {step === 3 && (
            <Button
              type="primary"
              size="large"
              onClick={saveStyle}
              loading={saving}
              disabled={saving}
              icon={<SaveOutlined />}
            >
              このスタイルを保存する
            </Button>
          )}
        </div>
      </div>

      {/* 全画面ブロッキングのローディング */}
      <Modal
        open={analyzing || loading}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
        bodyStyle={{ textAlign: 'center', padding: 32 }}
      >
        <Spin size="large" />
        <div style={{ marginTop: 12 }}>
          {analyzing ? '解析中です。しばらくお待ちください…' : '読み込み中…'}
        </div>
        <div style={{ marginTop: 16 }}>
          <Progress percent={Math.round(progress)} status="active" />
        </div>
        <TipsRotator tips={[...styleTips, ...waitingTips]} intervalMs={7000} fadeMs={300} />
      </Modal>
    </div>
  );
};

export default StyleNew;
